/**
 * SPA page views. GTM sends NO automatic page_view (GTM_NOTES.md): the site
 * pushes one explicit `page_view` per path change, with the address redacted
 * — SUP questions (?q=), Stripe session ids, login and gift tokens, the app's
 * handoff code and the like never reach GA4 — and the URL fragment dropped
 * (#id_token after Google sign-in). utm_*, gclid and ref stay: GA4 reads
 * campaign and Ads attribution from page_location. page_title is an English
 * name derived from the route (routeTitle); document.title is not changed.
 */
import { track } from "./dataLayer";
import { redactUrl } from "./redact";
import { analyticsReferrer } from "./referrer";

export { REDACTED_PARAMS, redactReferrer, redactUrl } from "./redact";

const SITE_LANGS = ["ru", "en", "cn", "ar", "es", "de"];
const AUTH_ROUTE_RE = /^\/(?:[a-z]{2}\/)?auth(?:\/|$)/;

export const isAuthCallbackPath = (pathname: string): boolean => AUTH_ROUTE_RE.test(pathname || "");

export const siteLangOf = (pathname: string): string | undefined => {
    const seg = (pathname || "").split("/")[1];
    return SITE_LANGS.includes(seg) ? seg : undefined;
};

let lastLocation: string | null = null;
// The page the last page_view was for: video events keep it (activityBridge.ts).
let lastPage: { pathname: string; route?: string } | null = null;

const ROUTE_WORDS: Record<string, string> = { ai: "AI", sup: "SUP", faq: "FAQ", zog: "ZOG", hr: "HR", pdf: "PDF" };

/**
 * An English page title from the route pattern — the same for every language
 * and every id: ":lang/my-courses-new/:courseId/:lessonId" → "My Courses New /
 * Course / Lesson", ":lang/account/consultations" → "Account / Consultations",
 * ":lang" → "Home". A parameter is named by itself without "Id"; ":lang" and
 * "*" are left out. document.title is the same on every page, so GA4 could not
 * tell pages apart by it. Empty when there is no route.
 */
export const routeTitle = (route: string | undefined): string => {
    if (!route) return "";
    if (route === "/" || route === ":lang") return "Home";
    const words = (seg: string): string =>
        seg
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .split(/[-_\s]+/)
            .filter(Boolean)
            .map((w) => ROUTE_WORDS[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
    return route
        .split("/")
        .map((seg) => {
            if (!seg || seg === "*" || seg === ":lang") return "";
            return seg.startsWith(":") ? words(seg.slice(1).replace(/_?id$/i, "")) : words(seg);
        })
        .filter(Boolean)
        .join(" / ");
};

/**
 * page_location (redacted) and page_route of the page on screen now — for
 * events that must keep the page they started on (video progress / stop).
 * The route is known once that path's page_view went out.
 */
export const currentPageParams = (): { page_location?: string; page_route?: string } => {
    try {
        const pathname = window.location.pathname;
        return {
            page_location: redactUrl(window.location.href) || undefined,
            page_route: lastPage && lastPage.pathname === pathname ? lastPage.route : undefined,
        };
    } catch {
        return {};
    }
};

/**
 * Push `page_view` for the current address. `route` is the route pattern
 * (app/providers/ActivityLog/lib/routePattern). Skipped on /auth/* callbacks
 * (tokens in the address) and when the redacted address did not change.
 */
export const trackPageView = (o: { route?: string } = {}): boolean => {
    let href = "";
    let pathname = "";
    try {
        href = window.location.href;
        pathname = window.location.pathname;
    } catch {
        return false;
    }
    if (isAuthCallbackPath(pathname)) return false;
    const location = redactUrl(href);
    if (!location || location === lastLocation) return false;
    let referrer = lastLocation;
    if (referrer === null) {
        // The first page view of this document: the site that sent the visitor
        // — or our own origin after a sign-in / payment hop (referrer.ts).
        try {
            referrer = analyticsReferrer(document.referrer);
        } catch {
            referrer = "";
        }
    }
    lastLocation = location;
    lastPage = { pathname, route: o.route || undefined };
    let title = routeTitle(o.route);
    if (!title) {
        try {
            title = document.title;
        } catch {
            title = "";
        }
    }
    return track("page_view", {
        page_location: location,
        page_referrer: referrer || undefined,
        page_title: title || undefined,
        page_route: o.route || undefined,
        site_lang: siteLangOf(pathname),
    });
};

/** Tests only. */
export const resetPageViewState = (): void => {
    lastLocation = null;
    lastPage = null;
};
